import { useAuth } from '@contexts/auth-provider';
import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import { buildLDUserContext } from '../../LaunchDarklyProvider.utils';

/**
 * Synchronizes LaunchDarkly user context with AuthProvider state.
 *
 * - Waits for auth to finish loading before identifying
 * - Identifies with user data (email, full name) when authenticated
 * - Includes selected module name and customerId (Manage only)
 * - Re-identifies as anonymous when user logs out
 * - Re-identifies when module or organization changes
 *
 * Must be called inside LaunchDarklyProvider, AuthProvider,
 * ModulesProvider, and OrganizationsProvider trees.
 */
export function useLDIdentify(): void {
  const { authData, isLoading } = useAuth();
  const { selectedModule } = useModules();
  const { selectedOrganization } = useOrganizations();
  const ldClient = useLDClient();

  const moduleName = selectedModule?.name;
  const customerId = selectedOrganization?.customerId;

  React.useEffect(() => {
    if (isLoading || !ldClient) {
      return;
    }

    const context = buildLDUserContext(authData, moduleName, customerId);
    ldClient.identify(context);
  }, [authData, ldClient, isLoading, moduleName, customerId]);
}
